import Phaser from 'phaser';
import { GAME_WIDTH } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';

/**
 * Creates Persian decorative borders and ornaments for the lobby.
 * Includes decorative borders, form frames, and corner decorations.
 */
export class PersianDecorations {
  private containers: Phaser.GameObjects.Container[] = [];
  private graphics: Phaser.GameObjects.Graphics[] = [];

  constructor(private scene: Phaser.Scene) {
    // Decorative border under the title (closer to form frame top at Y=170)
    this.containers.push(this.createBorder(GAME_WIDTH / 2, 155, 320, false));

    // Frame around the main form area
    // Top at ~170 (below title decoration, above "Your Name"), bottom at ~560
    this.createFormFrame(GAME_WIDTH / 2, 365, 430, 390);

    // Corner decorations (4 corners - positioned to frame the content)
    const cornerInset = 80;
    const topY = 130;
    const bottomY = 640;
    this.containers.push(this.createCornerDecoration(cornerInset, topY, 0)); // Top-left
    this.containers.push(this.createCornerDecoration(GAME_WIDTH - cornerInset, topY, Math.PI / 2)); // Top-right
    this.containers.push(this.createCornerDecoration(GAME_WIDTH - cornerInset, bottomY, Math.PI)); // Bottom-right
    this.containers.push(this.createCornerDecoration(cornerInset, bottomY, -Math.PI / 2)); // Bottom-left

    // Bottom decorative border (above the buttons)
    this.containers.push(this.createBorder(GAME_WIDTH / 2, 580, 400, true));
  }

  /**
   * Create a subtle decorative frame around the form area with filled background.
   */
  private createFormFrame(x: number, y: number, width: number, height: number): void {
    const graphics = this.scene.add.graphics();
    this.graphics.push(graphics);

    // Filled background - fully opaque so floating cards don't show through
    graphics.fillStyle(PERSIAN_THEME.colors.tableCenter, 1.0);
    graphics.fillRoundedRect(x - width / 2, y - height / 2, width, height, 15);

    // Add felt/fabric texture pattern to the background
    const textureGraphics = this.scene.add.graphics();
    this.graphics.push(textureGraphics);
    const left = x - width / 2;
    const top = y - height / 2;

    // Create a mask for the texture to stay within rounded rect
    const maskGraphics = this.scene.make.graphics({ x: 0, y: 0 });
    this.graphics.push(maskGraphics);
    maskGraphics.fillStyle(0xffffff);
    maskGraphics.fillRoundedRect(left, top, width, height, 15);
    const mask = maskGraphics.createGeometryMask();
    textureGraphics.setMask(mask);

    // Horizontal grain lines (felt weave effect)
    for (let i = 0; i < 60; i++) {
      const py = top + Math.random() * height;
      const lineWidth = 30 + Math.random() * 80;
      const px = left + Math.random() * (width - lineWidth);
      textureGraphics.lineStyle(1, 0x1a4a3a, Math.random() * 0.15 + 0.05);
      textureGraphics.lineBetween(px, py, px + lineWidth, py + (Math.random() - 0.5) * 2);
    }

    // Vertical grain lines
    for (let i = 0; i < 40; i++) {
      const px = left + Math.random() * width;
      const lineHeight = 20 + Math.random() * 50;
      const py = top + Math.random() * (height - lineHeight);
      textureGraphics.lineStyle(1, 0x0a2a1a, Math.random() * 0.1 + 0.03);
      textureGraphics.lineBetween(px, py, px + (Math.random() - 0.5) * 2, py + lineHeight);
    }

    // Lighter speckles (fabric fibers)
    for (let i = 0; i < 200; i++) {
      const px = left + Math.random() * width;
      const py = top + Math.random() * height;
      textureGraphics.fillStyle(0x4d7a67, Math.random() * 0.2 + 0.1);
      textureGraphics.fillCircle(px, py, Math.random() * 1.2 + 0.3);
    }

    // Darker speckles (shadows in fabric)
    for (let i = 0; i < 150; i++) {
      const px = left + Math.random() * width;
      const py = top + Math.random() * height;
      textureGraphics.fillStyle(0x051510, Math.random() * 0.15 + 0.05);
      textureGraphics.fillCircle(px, py, Math.random() * 1.5 + 0.5);
    }

    // Outer frame with rounded corners
    graphics.lineStyle(1, PERSIAN_THEME.colors.bronze, 0.3);
    graphics.strokeRoundedRect(x - width / 2, y - height / 2, width, height, 15);

    // Inner accent lines at corners
    const cornerLen = 30;
    const inset = 8;
    graphics.lineStyle(2, PERSIAN_THEME.colors.gold, 0.25);

    // Top-left corner accent
    graphics.beginPath();
    graphics.moveTo(x - width / 2 + inset, y - height / 2 + inset + cornerLen);
    graphics.lineTo(x - width / 2 + inset, y - height / 2 + inset);
    graphics.lineTo(x - width / 2 + inset + cornerLen, y - height / 2 + inset);
    graphics.strokePath();

    // Top-right corner accent
    graphics.beginPath();
    graphics.moveTo(x + width / 2 - inset - cornerLen, y - height / 2 + inset);
    graphics.lineTo(x + width / 2 - inset, y - height / 2 + inset);
    graphics.lineTo(x + width / 2 - inset, y - height / 2 + inset + cornerLen);
    graphics.strokePath();

    // Bottom-left corner accent
    graphics.beginPath();
    graphics.moveTo(x - width / 2 + inset, y + height / 2 - inset - cornerLen);
    graphics.lineTo(x - width / 2 + inset, y + height / 2 - inset);
    graphics.lineTo(x - width / 2 + inset + cornerLen, y + height / 2 - inset);
    graphics.strokePath();

    // Bottom-right corner accent
    graphics.beginPath();
    graphics.moveTo(x + width / 2 - inset - cornerLen, y + height / 2 - inset);
    graphics.lineTo(x + width / 2 - inset, y + height / 2 - inset);
    graphics.lineTo(x + width / 2 - inset, y + height / 2 - inset - cornerLen);
    graphics.strokePath();
  }

  /**
   * Create a Persian-style decorative border (horizontal).
   */
  private createBorder(x: number, y: number, width: number, subtle: boolean): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const height = subtle ? 20 : 24;
    const alpha = subtle ? 0.5 : 0.7;

    // Background bar
    const bg = this.scene.add.rectangle(0, 0, width, height, PERSIAN_THEME.colors.bronze, alpha);
    bg.setStrokeStyle(1, PERSIAN_THEME.colors.gold, 0.8);

    // Diamond accents along the border
    const diamondCount = Math.floor(width / 60);
    const diamondSpacing = width / (diamondCount + 1);
    const diamonds: Phaser.GameObjects.Graphics[] = [];

    for (let i = 1; i <= diamondCount; i++) {
      const diamond = this.scene.add.graphics();
      diamond.fillStyle(PERSIAN_THEME.colors.gold, 0.6);
      const dx = -width / 2 + i * diamondSpacing;
      const size = subtle ? 4 : 5;
      diamond.fillPoints(
        [
          { x: dx, y: -size },
          { x: dx + size, y: 0 },
          { x: dx, y: size },
          { x: dx - size, y: 0 },
        ],
        true
      );
      diamonds.push(diamond);
    }

    // End caps (small circles)
    const leftCap = this.scene.add.circle(-width / 2, 0, 4, PERSIAN_THEME.colors.gold, 0.7);
    const rightCap = this.scene.add.circle(width / 2, 0, 4, PERSIAN_THEME.colors.gold, 0.7);

    container.add([bg, ...diamonds, leftCap, rightCap]);
    return container;
  }

  /**
   * Create a corner decoration with Persian geometric pattern.
   */
  private createCornerDecoration(x: number, y: number, rotation: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setRotation(rotation);

    const graphics = this.scene.add.graphics();

    // L-shaped corner bracket
    graphics.lineStyle(2, PERSIAN_THEME.colors.gold, 0.5);
    graphics.beginPath();
    graphics.moveTo(0, 40);
    graphics.lineTo(0, 0);
    graphics.lineTo(40, 0);
    graphics.strokePath();

    // Inner L-shape
    graphics.lineStyle(1, PERSIAN_THEME.colors.bronze, 0.4);
    graphics.beginPath();
    graphics.moveTo(5, 30);
    graphics.lineTo(5, 5);
    graphics.lineTo(30, 5);
    graphics.strokePath();

    // Corner diamond
    graphics.fillStyle(PERSIAN_THEME.colors.gold, 0.5);
    const size = 6;
    graphics.fillPoints(
      [
        { x: 0, y: -size + 15 },
        { x: size, y: 15 },
        { x: 0, y: size + 15 },
        { x: -size, y: 15 },
      ],
      true
    );

    // Small accent dot
    graphics.fillStyle(PERSIAN_THEME.colors.bronze, 0.6);
    graphics.fillCircle(20, 20, 3);

    container.add(graphics);
    return container;
  }

  destroy(): void {
    this.containers.forEach((c) => c.destroy());
    this.graphics.forEach((g) => g.destroy());
    this.containers = [];
    this.graphics = [];
  }
}
